import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {
  ConfigurationService,
  IAppEndpoint,
  IAppSettings
} from '../configuration/configuration.service';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { OirErrorHandler } from '../error/error.handler';
import * as moment from 'moment';

export interface IOirRequest<T> {
  path?: string;
  requestUrl?: 'ApiEndpoint' | 'ApiEndpoint2' | 'CommsEndpoint' | string;
  requestAccessToken?: string;
  customHeader?: { [key: string]: string };
  body?: { [key: string]: any };
  params?: { [key: string]: any };
  query?: { [key: string]: any };
  contentType?: 'application/json' | string;
  responseType?: 'json' | 'arraybuffer' | 'text' | 'blob' | any;
  hideSpinner?: boolean;
  observe?: 'body' | 'events' | 'response' | any;
  removeContentType?: false | boolean;
}

@Injectable({ providedIn: 'root' })
export class OirHttpService {
  constructor(private http: HttpClient, private config: ConfigurationService <IAppSettings>) {}

  public get<T>(value: IOirRequest<T> | string): Observable<T> {
    const request: IOirRequest<T> = this.getRequest(value);

    return forkJoin([
      this.getHttpHeaders(request),
      this.getEndpoint(request)
    ]).pipe(
      switchMap(([headers, url]) =>
        this.http.get<T>(url, {
          headers,
          ...this.combineRequestOptions(request)
        })
      ),
      catchError(OirErrorHandler.handleHttpError as any)
    );
  }

  public post<T>(value: IOirRequest<T> | string): Observable<T> {
    const request: IOirRequest<T> = this.getRequest(value);

    return forkJoin([
      this.getHttpHeaders(request),
      this.getEndpoint(request)
    ]).pipe(
      switchMap(([headers, url]) =>
        this.http.post<T>(url, request.body, {
          headers,
          ...this.combineRequestOptions(request)
        })
      ),
      catchError(OirErrorHandler.handleHttpError as any)
    );
  }

  public put<T>(value: IOirRequest<T> | string): Observable<T> {
    const request: IOirRequest<T> = this.getRequest(value);

    return forkJoin([
      this.getHttpHeaders(request, 'PUT'),
      this.getEndpoint(request)
    ]).pipe(
      switchMap(([headers, url]) =>
        this.http.put<T>(url, request.body, {
          headers,
          ...this.combineRequestOptions(request)
        })
      ),
      catchError(OirErrorHandler.handleHttpError as any)
    );
  }

  public delete<T>(value: IOirRequest<T> | string): Observable<T> {
    const request: IOirRequest<T> = this.getRequest(value);

    return forkJoin([
      this.getHttpHeaders(request),
      this.getEndpoint(request)
    ]).pipe(
      switchMap(([headers, url]) =>
        this.http.request<T>('delete', url, {
          headers,
          params: this.getCombinedParams(request),
          body: request.body,
          observe: 'body'
        })
      ),
      catchError(OirErrorHandler.handleHttpError as any)
    );
  }

  private getCombinedParams(request: IOirRequest<any>): HttpParams {
    let params = new HttpParams();

    if (request.params) {
      Object.keys(request.params).forEach(
        key => (params = params.append(key, request.params[key]))
      );
    }

    if (request.hideSpinner) {
      params.append('hideSpinner', 'true');
    }

    return params;
  }

  public getEndpoint(request: IOirRequest<any>): Observable<string> {
    if (!request.query) {
      request.query = {};
    }

    request.query = {
      ...request.query,
      t: moment().format('x')
    };

    if (request.hideSpinner) {
      request.query.hideSpinner = true;
    }

    const apiUrl$ = request.requestUrl
      ? request.requestUrl === 'ApiEndpoint' ||
      request.requestUrl === 'ApiEndpoint2' ||
      request.requestUrl === 'CommsEndpoint'
        ? this.config
          .get(request.requestUrl)
          .pipe(map((endpoint: IAppEndpoint) => endpoint.url))
        : of(request.requestUrl)
      : this.config.get('ApiEndpoint').pipe(map(endpoint => endpoint.url));

    return apiUrl$.pipe(
      shareReplay(1),
      map(
        apiUrl => `${apiUrl}${request.path}${this.composeQuery(request) || ''}`
      )
    );
  }

  private getHttpHeaders(
    request: IOirRequest<any>,
    type: 'GET' | 'PUT' = 'GET'
  ): Observable<HttpHeaders> {
    const options = {
      'Content-Type': request.contentType || 'application/json',
      ...request.customHeader
    };

    if (request.requestAccessToken) {
      options['Authorization'] = `Bearer ${request.requestAccessToken}`;
    }

    if (request.requestUrl === 'CommsEndpoint' && this.config.getValue('CommsToken')) {
      options['Authorization'] = `Bearer ${this.config.getValue('CommsToken')}`;
    }

    if (type === 'PUT' || request.removeContentType) {
      delete options['Content-Type'];
    }

    return of(new HttpHeaders(options));
  }

  private combineRequestOptions<T>(request: IOirRequest<T>): any {
    const options = {
      params: this.getCombinedParams(request),
      responseType: request.responseType || 'json'
    } as any;

    if (request.observe) {
      options.observe = request.observe;
    }

    return options;
  }

  private composeQuery(request: IOirRequest<any>): string {
    const queryParams = [];
    if (request.query) {
      for (const key of Object.keys(request.query)) {
        queryParams.push(`${key}=${request.query[key]}`);
      }

      return (request.path.includes('?') ? '&' : '?') + queryParams.join('&');
    }
  }

  private getRequest<T>(value: IOirRequest<T> | string): IOirRequest<T> {
    return typeof value === 'string' ? { path: value } : value;
  }
}
